import { ComponentPropsWithoutRef, useCallback, useMemo } from 'react';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { SearchCollectionName } from 'api/search';
import {
    SearchListItem as SearchListItemPrimitive,
    SearchListItemProps,
} from 'components/search/SearchListItem';
import { useGlobalSearchResults } from 'components/search/useGlobalSearchResults';
import { twMerge } from 'tailwind-merge';

interface SearchResultsContainerProps extends ComponentPropsWithoutRef<'div'> {}
const SearchResultsContainer = (props: SearchResultsContainerProps) => {
    const { className, children, ...rest } = props;

    return (
        <div className={twMerge('flex flex-col', className)} {...rest}>
            {children}
        </div>
    );
};

interface SearchResultsTitleProps extends ComponentPropsWithoutRef<'p'> {}
const SearchResultsTitle = (props: SearchResultsTitleProps) => {
    const { className, children, ...rest } = props;

    return (
        <p className={twMerge('text-xs font-medium text-gray-400', className)} {...rest}>
            {children}
        </p>
    );
};

interface SearchResultsIconProps extends FontAwesomeIconProps {}
const SearchResultsIcon = (props: SearchResultsIconProps) => {
    const { className, ...rest } = props;

    return (
        <FontAwesomeIcon
            className={twMerge('text-gray-400 group-hover:text-blue-500', className)}
            {...rest}
        />
    );
};

interface SearchResultsListItemProps extends SearchListItemProps {}
const SearchResultsListItem = (props: SearchResultsListItemProps) => {
    const { className, children, ...rest } = props;

    return (
        <SearchListItemPrimitive
            className={twMerge('flex items-center gap-1.5 text-sm', className)}
            {...rest}
        >
            {children}
        </SearchListItemPrimitive>
    );
};

const useSearchResultsProps = <T extends SearchCollectionName>(resource: T) => {
    const { data } = useGlobalSearchResults();
    const items = useMemo(() => data[resource], [data, resource]);
    const isEmpty = useMemo(() => !items || items.found === 0, [items]);
    const getKey = useCallback((key: string | number) => `${resource}-${key}`, [resource]);

    return {
        getKey,
        isEmpty,
        data: items,
    };
};

const Root = SearchResultsContainer;
const Title = SearchResultsTitle;
const ListItem = SearchResultsListItem;
const Icon = SearchResultsIcon;

export {
    useSearchResultsProps,
    //
    SearchResultsContainer,
    SearchResultsTitle,
    SearchResultsListItem,
    SearchResultsIcon,
    //
    Root,
    Title,
    ListItem,
    Icon,
};
